import React, { useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import useBedbankRates from 'hooks/useBedbankRates'
import WalledContent from 'components/Common/WalledContent'
import BedbankSummaryPriceDetails from './BedbankSummaryPriceDetails'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import Group from 'components/utils/Group'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'

const SummaryPane = styled(Pane)`
  position: relative;
  min-width: ${rem(342)};
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  className?: string;
  offer: App.BedbankOffer;
  onViewPackageOptions?: () => void;
  rooms: Array<App.Occupants>;
  flightPrice: number;
  flightsEnabled: boolean;
  duration: number;
  loadingPrices?: boolean;
}

function BedbankOfferSummary(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    className,
    offer,
    onViewPackageOptions,
    rooms,
    loadingPrices,
    flightsEnabled,
    duration,
    flightPrice,
  } = props
  const priceTrackedRef = useRef(false)

  const [{ hotelOnlyRates, hotelBundleRates }] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)
  const sell = useBedbankOfferSell(offer, duration, rooms)
  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  const hasDates = checkInDate && checkOutDate
  const hasRates = hotelOnlyRates.length > 0
  const showPriceDetails = (hasDates && hasRates) || (!hasDates && sell)

  const lowestRate = hotelOnlyRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelOnlyRates[0])

  const lowestBundleRate = hotelBundleRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelBundleRates[0])

  return (
    <SummaryPane style={businessTravellerStickyBudgetBarStyle} border="neutral-six" borderRadius="XS" padding="28 20" shadow="bottom-small" className={className} data-testid="SummaryPane">
      <Group direction="vertical" gap={12}>

        <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing} wall={<TextButton kind="primary" fit="flex" onClick={onViewPackageOptions}>
          View package options
        </TextButton>}
        >
          {showPriceDetails &&
            <RelativeBlock>
              <LoadingIndicator floating opaque backdrop="grey-light" visible={loadingPrices} />
              <BedbankSummaryPriceDetails
                rate={lowestRate}
                bundleRate={lowestBundleRate}
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                onViewPackageOptions={onViewPackageOptions}
                priceTrackedRef={priceTrackedRef}
              />
            </RelativeBlock>
          }
        </WalledContent>
      </Group>
    </SummaryPane>
  )
}

export default React.memo(BedbankOfferSummary)
